import React, { useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import './CollapsibleSearch.css';

export interface CollapsibleSearchProps {
  /** Current search value */
  value: string;
  /** Change handler for search value */
  onChange: (value: string) => void;
  /** Placeholder text when expanded */
  placeholder?: string;
  /** Collapsed label text */
  collapsedLabel?: string;
  /** Optional class name */
  className?: string;
  /** Auto-expand on mount */
  autoExpand?: boolean;
}

/**
 * Collapsible search input that shows as a compact button when collapsed
 * and expands to a full search input when clicked.
 */
export const CollapsibleSearch: React.FC<CollapsibleSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search...',
  collapsedLabel = 'Search...',
  className = '',
  autoExpand = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(autoExpand);
  const inputRef = useRef<HTMLInputElement>(null);

  const expandSearch = () => {
    setIsExpanded(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const collapseSearch = () => {
    setIsExpanded(false);
    onChange('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      collapseSearch();
    }
  };

  return (
    <div className={`collapsible-search ${className}`}>
      {!isExpanded ? (
        <button
          className="collapsible-search__trigger"
          onClick={expandSearch}
          aria-label="Expand search"
          type="button"
        >
          <Search className="collapsible-search__icon" />
          <span className="collapsible-search__label">{collapsedLabel}</span>
        </button>
      ) : (
        <div className="collapsible-search__expanded">
          <div className="collapsible-search__input-wrapper">
            <Search className="collapsible-search__input-icon" />
            <input
              ref={inputRef}
              type="text"
              className="collapsible-search__input"
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className="collapsible-search__close"
              onClick={collapseSearch}
              aria-label="Close search"
              type="button"
            >
              <X className="collapsible-search__close-icon" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleSearch;
